/**
 * Lightweight HTTP-error event bus.
 *
 * Mirrors Angular's centralized httpService.catchError → toastService.addErrorToast()
 * pattern. The Axios response interceptor publishes errors here; a React listener
 * component in Home.tsx subscribes and shows a BADS toast.
 *
 * Features:
 *  - 500 ms debounce so Promise.all bursts produce a single toast
 *  - One-shot suppression for callers that show their own toast
 *  - 403 handler registration for AppContext wiring
 */

type ErrorListener = (message: string, timeout?: number) => void;
type Handler403 = () => void;

const DEBOUNCE_MS = 500;

let _listener: ErrorListener | null = null;
let _handler403: Handler403 | null = null;
let _suppressed = false;
let _debounceTimer: ReturnType<typeof setTimeout> | null = null;

/** Subscribe to HTTP error messages. Only one listener at a time (Home.tsx). */
export function onHttpError(listener: ErrorListener): () => void {
  _listener = listener;
  return () => {
    if (_listener === listener) _listener = null;
  };
}

/**
 * Publish an error message from the Axios interceptor.
 * Debounced so concurrent failures (e.g. SearchBar's Promise.all) show one toast.
 */
export function publishHttpError(message: string, timeout?: number): void {
  if (_suppressed) {
    _suppressed = false;
    return;
  }

  if (_debounceTimer !== null) {
    clearTimeout(_debounceTimer);
  }

  _debounceTimer = setTimeout(() => {
    _debounceTimer = null;
    if (timeout === undefined) {
      _listener?.(message);
      return;
    }
    _listener?.(message, timeout);
  }, DEBOUNCE_MS);
}

/**
 * Suppress the *next* error published to the bus.
 * Call this immediately before an HTTP request whose caller shows its own toast.
 */
export function suppressNextError(): void {
  _suppressed = true;
}

/** Register the 403 handler (called once from AppContext). */
export function register403Handler(handler: Handler403): void {
  _handler403 = handler;
}

/** Trigger the registered 403 handler (called from the Axios interceptor). */
export function trigger403(): void {
  _handler403?.();
}

/** Reset all internal state – for tests only. */
export function _resetForTesting(): void {
  _listener = null;
  _handler403 = null;
  _suppressed = false;
  if (_debounceTimer !== null) {
    clearTimeout(_debounceTimer);
    _debounceTimer = null;
  }
}
